import dayjs from "dayjs";
import nodeAPI from "../../service/ApiServiceNode";
import { API_CHECKLIST_TASK } from "../../constants";
import { resolveDate } from "../../components/Tasks/VoiceTaskCreate/date-resolver";
import { TASK_PRIORITY_VALUES, DEFAULT_TASK_PRIORITY } from "../../components/Tasks/task-priority-config";
import { TASK_STATUS } from "../../components/Tasks/taskLifecycle";

const VALID_STATUSES = Object.values(TASK_STATUS);
const MAX_BATCH = 20;

function unwrap(response) {
    return response?.data?.data || response?.data || {};
}

function workspace(ctx) {
    return ctx.store.getState().taskWorkspace || {};
}

function taskLabel(task) {
    return task?.name || task?.title || `Task ${task?.id || ""}`.trim();
}

function currentUser(ctx) {
    return workspace(ctx).currentUser || null;
}

function resolveTaskRefs({ task_numbers: taskNumbers, taskNumbers: camelNumbers, task_ids: taskIds, taskIds: camelIds } = {}, ctx) {
    const visibleTasks = workspace(ctx).visibleTasks || [];
    const ids = new Set((taskIds || camelIds || []).map((id) => String(id)));
    const numbers = taskNumbers || camelNumbers || [];
    for (const number of numbers) {
        const index = Number(number) - 1;
        const task = visibleTasks[index];
        if (task?.id) ids.add(String(task.id));
    }
    const resolved = Array.from(ids).map((id) => (
        visibleTasks.find((task) => String(task.id) === id) || { id: Number(id), name: `Task ${id}` }
    ));
    return resolved.filter((task) => task?.id);
}

function normalizePriority(priority) {
    if (!priority) return null;
    const upper = String(priority).toUpperCase();
    return TASK_PRIORITY_VALUES.includes(upper) ? upper : null;
}

function normalizeStatus(status) {
    if (!status) return null;
    const upper = String(status).toUpperCase();
    return VALID_STATUSES.includes(upper) ? upper : null;
}

function normalizeDueDate(value) {
    if (!value) return null;
    const resolved = resolveDate(value);
    if (resolved) return resolved;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

function normalizeDraft(raw = {}, index = 0) {
    const dueDate = normalizeDueDate(raw.dueDate || raw.due_date || raw.dueDatePhrase || raw.due_date_phrase);
    const priority = normalizePriority(raw.priority) || DEFAULT_TASK_PRIORITY;
    const name = String(raw.name || raw.title || "").trim();
    const draft = {
        number: index + 1,
        name,
        description: raw.description || null,
        priority,
        dueDate,
        dueDateLabel: raw.dueDatePhrase || raw.due_date_phrase || raw.dueDate || raw.due_date || null,
        assigneeId: raw.assigneeId || raw.assignee_id || null,
        assigneeLabel: raw.assigneeLabel || raw.assignee_name || null,
        verifierId: raw.verifierId || raw.verifier_id || null,
        projectId: raw.projectId || raw.project_id || null,
        taskType: raw.taskType || raw.task_type || "standard",
        selected: raw.selected !== false
    };
    draft.ready = Boolean(draft.name && draft.dueDate);
    draft.missing = [
        !draft.name ? "name" : null,
        !draft.dueDate ? "dueDate" : null
    ].filter(Boolean);
    return draft;
}

function readWorkspace(_args, ctx) {
    const state = workspace(ctx);
    return {
        ok: true,
        screen: state.taskType,
        view: state.viewMode,
        currentUser: state.currentUser || null,
        selectedTask: state.selectedTask || null,
        visibleTasks: (state.visibleTasks || []).map((task, index) => ({
            number: task.number || index + 1,
            id: task.id,
            name: taskLabel(task),
            status: task.status || null,
            priority: task.priority || null,
            dueDate: task.dueDate || null,
            assigneeId: task.assigneeId || null,
            assigneeName: task.assigneeName || null
        }))
    };
}

function stageMany({ drafts = [] }, ctx) {
    if (!Array.isArray(drafts) || drafts.length === 0) {
        return { ok: false, detail: "drafts are required" };
    }
    const staged = drafts.slice(0, MAX_BATCH).map(normalizeDraft);
    ctx.actions.setMultiTaskDrafts(staged);
    return {
        ok: true,
        count: staged.length,
        ready: staged.filter((draft) => draft.ready).length,
        drafts: staged
    };
}

async function commitMany({ draft_numbers: draftNumbers, draftNumbers: camelNumbers } = {}, ctx) {
    const state = ctx.store.getState();
    const drafts = state.taskAction.multiDrafts || [];
    const numbers = draftNumbers || camelNumbers || [];
    const selectedNumbers = numbers.length ? new Set(numbers.map((number) => Number(number))) : null;
    const selected = drafts.filter((draft) => draft.selected !== false && (!selectedNumbers || selectedNumbers.has(draft.number)));
    const invalid = selected.filter((draft) => !draft.ready);
    if (selected.length === 0) return { ok: false, detail: "No staged drafts selected" };
    if (invalid.length > 0) {
        return {
            ok: false,
            detail: "Some selected drafts are missing required fields",
            drafts: invalid.map((draft) => ({ number: draft.number, missing: draft.missing }))
        };
    }

    ctx.actions.setSaving(true);
    try {
        const created = [];
        for (const draft of selected) {
            const payload = {
                name: draft.name,
                description: draft.description,
                priority: draft.priority,
                dueDate: dayjs(draft.dueDate).toISOString(),
                projectId: draft.projectId,
                taskType: draft.taskType || "standard",
                assigneeId: draft.assigneeId,
                verifierId: draft.verifierId,
                source: "voice"
            };
            const response = await nodeAPI.post(`${API_CHECKLIST_TASK}/standalone`, payload);
            created.push(unwrap(response));
        }
        const result = { ok: true, createdCount: created.length, created };
        ctx.actions.setTaskActionResult(result);
        ctx.actions.setSaved(true);
        return result;
    } catch (error) {
        const detail = error?.response?.data?.data?.message || error?.response?.data?.message || error?.message || "create failed";
        ctx.actions.setTaskActionError(detail);
        return { ok: false, detail };
    } finally {
        ctx.actions.setSaving(false);
    }
}

function normalizeChanges(args = {}, ctx) {
    const raw = args.changes || args;
    const me = currentUser(ctx);
    const assigneeId =
        raw.assigneeId || raw.assignee_id || (raw.assignee === "me" || raw.assigneeName === "me" ? me?.id : null);
    const dueDate = normalizeDueDate(raw.dueDate || raw.due_date || raw.dueDatePhrase || raw.due_date_phrase);
    const priority = normalizePriority(raw.priority);
    const status = normalizeStatus(raw.status);
    return {
        name: raw.name || raw.title || null,
        description: raw.description || null,
        dueDate,
        dueDateLabel: raw.dueDatePhrase || raw.due_date_phrase || raw.dueDate || raw.due_date || null,
        priority,
        status,
        assigneeId,
        assigneeLabel: raw.assigneeLabel || raw.assigneeName || (assigneeId && me?.id === assigneeId ? me.name : null),
        tagIds: Array.isArray(raw.tagIds || raw.tag_ids) ? raw.tagIds || raw.tag_ids : null,
        tagAction: raw.tagAction || raw.tag_action || "add",
        watcherUserIds: Array.isArray(raw.watcherUserIds || raw.watcher_user_ids) ? raw.watcherUserIds || raw.watcher_user_ids : null,
        watcherWatchType: raw.watcherWatchType || raw.watcher_watch_type || "ALL"
    };
}

function changedColumns(changes) {
    return [
        changes.name ? "name" : null,
        changes.description ? "description" : null,
        changes.dueDate ? "due date" : null,
        changes.priority ? "priority" : null,
        changes.status ? "status" : null,
        changes.assigneeId ? "assignee" : null,
        changes.tagIds?.length ? "tags" : null,
        changes.watcherUserIds?.length ? "watchers" : null
    ].filter(Boolean);
}

function stageUpdate(args = {}, ctx) {
    const tasks = resolveTaskRefs(args, ctx);
    if (tasks.length === 0) return { ok: false, detail: "No matching visible tasks found" };
    const changes = normalizeChanges(args, ctx);
    const columns = changedColumns(changes);
    if (columns.length === 0) return { ok: false, detail: "No supported changes were provided" };

    const proposal = {
        id: `proposal-${Date.now()}`,
        tasks: tasks.map((task) => ({
            id: task.id,
            number: task.number || (workspace(ctx).visibleTasks || []).findIndex((item) => String(item.id) === String(task.id)) + 1,
            name: taskLabel(task),
            current: {
                status: task.status || null,
                priority: task.priority || null,
                dueDate: task.dueDate || null,
                assigneeId: task.assigneeId || null,
                assigneeName: task.assigneeName || null
            }
        })),
        changes,
        columns,
        unsupported: []
    };
    ctx.actions.setTaskEditProposal(proposal);
    return { ok: true, proposal };
}

async function applyUpdate(_args = {}, ctx) {
    const proposal = ctx.store.getState().taskAction.editProposal;
    if (!proposal?.tasks?.length) return { ok: false, detail: "No staged proposal to apply" };
    const taskIds = proposal.tasks.map((task) => task.id);
    const changes = proposal.changes || {};
    const results = [];

    ctx.actions.setSaving(true);
    try {
        if (changes.assigneeId) {
            const response = await nodeAPI.post(`${API_CHECKLIST_TASK}/bulk/reassign`, {
                taskIds,
                assigneeId: changes.assigneeId,
                reason: "Voice Copilot bulk edit"
            });
            results.push({ field: "assignee", result: unwrap(response) });
            ctx.actions.patchWorkspaceTasks(taskIds, {
                assigneeId: changes.assigneeId,
                assigneeName: changes.assigneeLabel || `User ${changes.assigneeId}`
            });
        }
        if (changes.status) {
            const response = await nodeAPI.post(`${API_CHECKLIST_TASK}/bulk/status`, {
                taskIds,
                status: changes.status
            });
            results.push({ field: "status", result: unwrap(response) });
            ctx.actions.patchWorkspaceTasks(taskIds, { status: changes.status });
        }
        if (changes.priority) {
            const response = await nodeAPI.post(`${API_CHECKLIST_TASK}/bulk/priority`, {
                taskIds,
                priority: changes.priority
            });
            results.push({ field: "priority", result: unwrap(response) });
            ctx.actions.patchWorkspaceTasks(taskIds, { priority: changes.priority });
        }
        if (changes.tagIds?.length) {
            const response = await nodeAPI.post(`${API_CHECKLIST_TASK}/bulk/tags`, {
                taskIds,
                tagIds: changes.tagIds,
                action: changes.tagAction || "add"
            });
            results.push({ field: "tags", result: unwrap(response) });
        }
        if (changes.watcherUserIds?.length) {
            for (const taskId of taskIds) {
                const response = await nodeAPI.post(`${API_CHECKLIST_TASK}/${taskId}/watchers`, {
                    userIds: changes.watcherUserIds,
                    watchType: changes.watcherWatchType || "ALL"
                });
                results.push({ field: "watchers", taskId, result: unwrap(response) });
            }
        }
        if (changes.dueDate || changes.name || changes.description) {
            for (const taskId of taskIds) {
                const payload = {};
                if (changes.dueDate) payload.due_date = dayjs(changes.dueDate).toISOString();
                if (changes.name) payload.name = changes.name;
                if (changes.description) payload.description = changes.description;
                const response = await nodeAPI.put(`${API_CHECKLIST_TASK}/${taskId}`, payload);
                results.push({ field: "task", taskId, result: unwrap(response) });
            }
            if (changes.dueDate) ctx.actions.patchWorkspaceTasks(taskIds, { dueDate: changes.dueDate });
            if (changes.name) ctx.actions.patchWorkspaceTasks(taskIds, { name: changes.name });
            if (changes.description) ctx.actions.patchWorkspaceTasks(taskIds, { description: changes.description });
        }

        const result = { ok: true, updatedCount: taskIds.length, results };
        ctx.actions.setTaskActionResult(result);
        return result;
    } catch (error) {
        const detail = error?.response?.data?.data?.message || error?.response?.data?.message || error?.message || "update failed";
        ctx.actions.setTaskActionError(detail);
        return { ok: false, detail };
    } finally {
        ctx.actions.setSaving(false);
    }
}

function reviewStatus(action) {
    const normalized = String(action || "").toLowerCase();
    if (["accept", "approve", "accepted"].includes(normalized)) return TASK_STATUS.ACCEPTED;
    if (["done", "complete", "completed"].includes(normalized)) return TASK_STATUS.DONE;
    if (["block", "blocked"].includes(normalized)) return TASK_STATUS.BLOCKED;
    if (["request_changes", "changes_requested", "revise"].includes(normalized)) return TASK_STATUS.CHANGES_REQUESTED;
    if (["reopen", "start", "resume"].includes(normalized)) return TASK_STATUS.IN_PROGRESS;
    return null;
}

async function reviewAction(args = {}, ctx) {
    const action = String(args.action || "").toLowerCase();
    const tasks = resolveTaskRefs(args, ctx);
    if (tasks.length === 0) return { ok: false, detail: "No matching visible tasks found" };

    if (action === "open") {
        const task = tasks[0];
        if (typeof window !== "undefined") {
            window.open(task.url || `/tasks/my-tasks?taskId=${encodeURIComponent(task.id)}`, "hrp_voice_task_review");
        }
        ctx.actions.setTaskActionResult({ ok: true, openedTask: task });
        return { ok: true, openedTask: task };
    }

    const status = reviewStatus(action);
    if (!status) return { ok: false, detail: `Unsupported review action: ${action}` };

    const proposal = stageUpdate({
        task_ids: tasks.map((task) => task.id),
        changes: { status }
    }, ctx);
    if (!proposal.ok) return proposal;
    return applyUpdate({}, ctx);
}

export const taskActionTools = [
    {
        key: "task_workspace.read",
        schema: {
            type: "function",
            description: "Read the currently visible numbered task workspace so task numbers can be resolved safely.",
            parameters: { type: "object", properties: {} }
        },
        executor: readWorkspace
    },
    {
        key: "task.stage_many",
        schema: {
            type: "function",
            description: "Stage multiple task drafts for user review before creating them.",
            parameters: {
                type: "object",
                properties: {
                    drafts: {
                        type: "array",
                        items: {
                            type: "object",
                            properties: {
                                name: { type: "string" },
                                description: { type: "string" },
                                priority: { type: "string", enum: TASK_PRIORITY_VALUES },
                                dueDate: { type: "string" },
                                dueDatePhrase: { type: "string" },
                                assigneeId: { type: "number" },
                                verifierId: { type: "number" },
                                projectId: { type: "number" },
                                taskType: { type: "string", enum: ["standard", "approval"] }
                            },
                            required: ["name"]
                        },
                        minItems: 1,
                        maxItems: MAX_BATCH
                    }
                },
                required: ["drafts"]
            }
        },
        executor: stageMany
    },
    {
        key: "task.commit_many",
        schema: {
            type: "function",
            description: "Create selected staged task drafts after explicit user confirmation.",
            parameters: {
                type: "object",
                properties: {
                    draft_numbers: {
                        type: "array",
                        items: { type: "number" }
                    }
                }
            }
        },
        executor: commitMany
    },
    {
        key: "task.stage_update",
        schema: {
            type: "function",
            description: "Stage one-task or multi-task edit proposals using visible task numbers or task IDs. Does not write.",
            parameters: {
                type: "object",
                properties: {
                    task_numbers: { type: "array", items: { type: "number" } },
                    task_ids: { type: "array", items: { type: "number" } },
                    changes: {
                        type: "object",
                        properties: {
                            name: { type: "string" },
                            description: { type: "string" },
                            dueDate: { type: "string" },
                            dueDatePhrase: { type: "string" },
                            priority: { type: "string", enum: TASK_PRIORITY_VALUES },
                            status: { type: "string", enum: VALID_STATUSES },
                            assigneeId: { type: "number" },
                            assignee: { type: "string" },
                            tagIds: { type: "array", items: { type: "number" } },
                            tagAction: { type: "string", enum: ["add", "remove", "set"] },
                            watcherUserIds: { type: "array", items: { type: "number" } },
                            watcherWatchType: { type: "string", enum: ["ALL", "COMMENTS_ONLY", "STATUS_ONLY"] }
                        }
                    }
                }
            }
        },
        executor: stageUpdate
    },
    {
        key: "task.apply_update",
        schema: {
            type: "function",
            description: "Apply the staged single-task or bulk edit proposal after explicit user confirmation.",
            parameters: { type: "object", properties: {} }
        },
        executor: applyUpdate
    },
    {
        key: "task.review_action",
        schema: {
            type: "function",
            description: "Apply review actions to visible numbered tasks, or open one task, after explicit user confirmation.",
            parameters: {
                type: "object",
                properties: {
                    action: {
                        type: "string",
                        enum: ["open", "accept", "approve", "done", "block", "request_changes", "reopen"]
                    },
                    task_numbers: { type: "array", items: { type: "number" } },
                    task_ids: { type: "array", items: { type: "number" } }
                },
                required: ["action"]
            }
        },
        executor: reviewAction
    }
];
